import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Alert, Button, Card, CardBody, Input, Label, Table } from "reactstrap"

import Modal from "components/Modal/MaterialModal"
import toastHandler from "toastHandler"
import {
  getTablaGeneralConfigEdicion,
  getTablaGeneralConfig,
  getTablasGeneralesCatalogos,
  saveTablaGeneralConfig,
  getEsquemasTablaExterna,
  getTablasDeEsquema,
  getColumnasTablaExterna,
  agregarColumnaActivo,
} from "api"
import { COMPONENT_NAMES } from "pages/Agro/Configuracion/Catalogos"

const CAMPOS = [
  "codNum",
  "codAlfa",
  "descripcion",
  "valorInt",
  "valorFloat",
  "valorStr",
  "orden",
]
const FORMATOS = [
  "",
  "texto",
  "mayuscula",
  "minuscula",
  "numerico",
  "entero",
  "monto",
  "porcentaje",
  "periodoAAAAMM",
  "booleano",
  "datetime",
  "combo",
]

const dialogStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(1000px, 96vw)",
  maxHeight: "92vh",
  overflowY: "auto",
}

const configInicial = {
  titulo: "",
  descripcion: "",
  modoBorrado: "L",
  permiteAlta: true,
  permiteEdicion: true,
  ocultarInactivos: false,
  nombreComponente: null,
  tipoCatalogo: "NATIVA",
  orden: 0,
}

// Campos por defecto (mismos que el fallback del backend) para precargar el editor
// cuando el catálogo todavía no tiene configuración propia.
const CAMPOS_DEFAULT = [
  ["codNum", "Id", ""],
  ["descripcion", "Descripción", "texto"],
  ["codAlfa", "Código alfa", "texto"],
  ["valorFloat", "ValorFloat", "numerico"],
  ["valorInt", "ValorInt", "numerico"],
  ["valorStr", "ValorString", "texto"],
].map(([campo, etiqueta, formato], i) => ({
  campo,
  etiqueta,
  visibleGrilla: true,
  visibleForm: true,
  editable: true,
  autoincremento: false,
  requerido: false,
  filtro: false,
  formato,
  orden: i + 1,
}))

const ConfigCatalogoModal = ({
  nro,
  codNum,
  descripcion,
  modal,
  toggle,
  onSaved,
  readOnly = false,
  tablas = [],
}) => {
  const { setSuccess, setError } = toastHandler()
  const [cfg, setCfg] = useState(configInicial)
  const [campos, setCampos] = useState([])
  const [nuevoNroTabla, setNuevoNroTabla] = useState(null)
  const [guardando, setGuardando] = useState(false)

  const [esquemas, setEsquemas] = useState([])
  const [tablasDelEsquema, setTablasDelEsquema] = useState([])
  const [esquemaSeleccionado, setEsquemaSeleccionado] = useState("")
  const [tablaSeleccionada, setTablaSeleccionada] = useState("")
  const [pendienteActivo, setPendienteActivo] = useState(null)
  const [importando, setImportando] = useState(false)
  const [catalogosDisponibles, setCatalogosDisponibles] = useState([])
  const [camposPorCatalogo, setCamposPorCatalogo] = useState({})
  // Recuerda, dentro de esta sesión del modal, qué campos había cargados para
  // cada tipoCatalogo ya visitado (p.ej. Interna -> Tabla Externa -> Interna
  // debe restaurar los campos de Interna, no perderlos).
  const [camposPorTipo, setCamposPorTipo] = useState({})

  const cargarCamposDeCatalogo = nroTablaOrigen => {
    if (!nroTablaOrigen || camposPorCatalogo[nroTablaOrigen]) return
    getTablaGeneralConfig(nroTablaOrigen)
      .then(data =>
        setCamposPorCatalogo(prev => ({
          ...prev,
          [nroTablaOrigen]: data?.campos || [],
        })),
      )
      .catch(setError)
  }

  useEffect(() => {
    if (!modal || !nro) return
    setNuevoNroTabla(codNum != null ? Number(codNum) : null)
    setPendienteActivo(null)
    setGuardando(false)
    getEsquemasTablaExterna().then(setEsquemas).catch(setError)
    getTablasGeneralesCatalogos().then(setCatalogosDisponibles).catch(setError)
    getTablaGeneralConfigEdicion(nro)
      .then(data => {
        setCfg({
          titulo: data?.titulo ?? "",
          descripcion: data?.descripcion ?? "",
          modoBorrado: data?.modoBorrado ?? "L",
          permiteAlta: data?.permiteAlta ?? true,
          permiteEdicion: data?.permiteEdicion ?? true,
          ocultarInactivos: data?.ocultarInactivos ?? false,
          nombreComponente: data?.nombreComponente ?? null,
          tipoCatalogo: data?.tipoCatalogo ?? "NATIVA",
          orden: data?.orden ?? 0,
        })
        const guardados = data?.campos || []
        const camposCargados = guardados.length
          ? guardados.map(c => ({
              campo: c.campo,
              etiqueta: c.etiqueta ?? "",
              visibleGrilla: c.visibleGrilla ?? true,
              visibleForm: c.visibleForm ?? true,
              editable: c.editable ?? true,
              autoincremento: c.autoincremento ?? false,
              requerido: c.requerido ?? false,
              filtro: c.filtro ?? false,
              formato: c.formato ?? "",
              comboNroTabla: c.comboNroTabla ?? null,
              comboCampoEtiqueta: c.comboCampoEtiqueta ?? "",
              comboCampoValor: c.comboCampoValor ?? "",
              orden: c.orden ?? 0,
            }))
          : data?.tipoCatalogo === "TABLA_EXTERNA"
            ? []
            : CAMPOS_DEFAULT.map(c => ({ ...c }))
        setCampos(camposCargados)
        setCamposPorTipo({ [data?.tipoCatalogo ?? "NATIVA"]: camposCargados })
        camposCargados
          .filter(c => c.formato === "combo" && c.comboNroTabla)
          .forEach(c => cargarCamposDeCatalogo(c.comboNroTabla))
        const [esquemaActual, tablaActual] = (data?.nombreTabla || "").split(
          ".",
        )
        setEsquemaSeleccionado(esquemaActual || "")
        setTablaSeleccionada(tablaActual || "")
        if (esquemaActual) {
          getTablasDeEsquema(esquemaActual)
            .then(setTablasDelEsquema)
            .catch(setError)
        } else {
          setTablasDelEsquema([])
        }
      })
      .catch(setError)
  }, [modal, nro])

  const setCfgField = (field, value) => setCfg({ ...cfg, [field]: value })

  const esTablaExterna = cfg.tipoCatalogo === "TABLA_EXTERNA"

  const handleEsquemaChange = esquema => {
    setEsquemaSeleccionado(esquema)
    setTablaSeleccionada("")
    setTablasDelEsquema([])
    if (esquema) {
      getTablasDeEsquema(esquema).then(setTablasDelEsquema).catch(setError)
    }
  }

  const aplicarColumnasImportadas = columnasImportadas => {
    setCampos(prev => {
      const existentes = new Set(prev.map(c => c.campo))
      const nuevos = columnasImportadas
        .filter(c => !existentes.has(c.nombre))
        .map((c, i) => {
          // "activo" es el campo de sistema para el borrado lógico: se ve en la
          // grilla (como check), pero no se edita a mano en el alta/edición —
          // se crea siempre en 1 (ver TablasGenerales.js, fila nueva de Tabla Externa).
          const esActivo = c.nombre.toLowerCase() === "activo"
          return {
            campo: c.nombre,
            etiqueta: c.nombre,
            visibleGrilla: true,
            visibleForm: !c.esPk && !esActivo,
            editable: !c.esPk && !esActivo,
            autoincremento: c.esPk,
            requerido: false,
            filtro: false,
            formato: /^(bit|boolean)$/i.test(c.tipo || "")
              ? "booleano"
              : /^(datetime|datetime2|timestamp)$/i.test(c.tipo || "")
                ? "datetime"
                : "",
            orden: prev.length + i + 1,
          }
        })
      return [...prev, ...nuevos]
    })
  }

  const importarColumnas = () => {
    if (!esquemaSeleccionado || !tablaSeleccionada) return
    setImportando(true)
    getColumnasTablaExterna(esquemaSeleccionado, tablaSeleccionada)
      .then(data => {
        if (!data.tieneActivo) {
          setPendienteActivo({ columnas: data.columnas })
          return
        }
        aplicarColumnasImportadas(data.columnas)
        setSuccess()
      })
      .catch(setError)
      .finally(() => setImportando(false))
  }

  const confirmarActivo = modo => {
    if (modo === "F") {
      setCfgField("modoBorrado", "F")
      aplicarColumnasImportadas(pendienteActivo.columnas)
      setPendienteActivo(null)
      setSuccess()
      return
    }
    setImportando(true)
    agregarColumnaActivo(esquemaSeleccionado, tablaSeleccionada)
      .then(() =>
        getColumnasTablaExterna(esquemaSeleccionado, tablaSeleccionada),
      )
      .then(data => {
        setCfgField("modoBorrado", "L")
        aplicarColumnasImportadas(data.columnas)
        setPendienteActivo(null)
        setSuccess()
      })
      .catch(setError)
      .finally(() => setImportando(false))
  }

  const setCampoField = (index, field, value) =>
    setCampos(
      campos.map((c, i) => {
        if (i !== index) return c
        const actualizado = { ...c, [field]: value }
        // El autoincremento solo aplica al campo codNum.
        if (field === "campo" && value !== "codNum")
          actualizado.autoincremento = false
        // Autoincremento: el valor lo asigna el sistema; el campo deja de ser
        // editable y el formato se fija en entero (miles, sin decimales).
        if (actualizado.autoincremento) {
          actualizado.editable = false
          actualizado.formato = "entero"
        }
        // Un campo que no se ve ni en la grilla ni en el form no puede ser editable.
        if (!actualizado.visibleGrilla && !actualizado.visibleForm)
          actualizado.editable = false
        return actualizado
      }),
    )

  const agregarCampo = () => {
    const usados = campos.map(c => c.campo)
    const disponible = CAMPOS.find(c => !usados.includes(c)) || "codNum"
    setCampos([
      ...campos,
      {
        campo: disponible,
        etiqueta: "",
        visibleGrilla: true,
        visibleForm: true,
        editable: true,
        autoincremento: false,
        requerido: false,
        filtro: false,
        formato: "",
        orden: campos.length + 1,
      },
    ])
  }

  const quitarCampo = index => setCampos(campos.filter((_, i) => i !== index))

  const guardar = () => {
    if (readOnly || guardando) return
    // Validar colisión de NroTabla
    if (nuevoNroTabla != null && nuevoNroTabla !== Number(codNum)) {
      const colision = tablas.some(
        t => t.codNum === nuevoNroTabla && Number(t.codNum) !== Number(codNum),
      )
      if (colision) {
        setError("El número de tabla " + nuevoNroTabla + " ya está en uso")
        return
      }
    }
    if (esTablaExterna && (!esquemaSeleccionado || !tablaSeleccionada)) {
      setError(
        "Debe seleccionar esquema y tabla para un catálogo de Tabla Externa",
      )
      return
    }
    const dto = {
      nroTabla: nro,
      nuevoNroTabla:
        nuevoNroTabla != null && nuevoNroTabla !== Number(codNum)
          ? nuevoNroTabla
          : null,
      titulo: cfg.titulo || null,
      descripcion: cfg.descripcion || null,
      modoBorrado: cfg.modoBorrado || "L",
      permiteAlta: cfg.permiteAlta,
      permiteEdicion: cfg.permiteEdicion,
      ocultarInactivos: cfg.ocultarInactivos,
      tipoCatalogo: cfg.tipoCatalogo,
      nombreComponente:
        cfg.tipoCatalogo === "COMPONENTE_EXTERNO" ? cfg.nombreComponente : null,
      nombreTabla: esTablaExterna
        ? `${esquemaSeleccionado}.${tablaSeleccionada}`
        : null,
      orden: Number(cfg.orden) || 0,
      campos: campos.map(c => ({
        ...c,
        orden: Number(c.orden) || 0,
        formato: c.formato || null,
      })),
    }
    setGuardando(true)
    saveTablaGeneralConfig(nro, dto)
      .then(() => {
        setSuccess()
        onSaved && onSaved()
        toggle()
      })
      .catch(setError)
      .finally(() => setGuardando(false))
  }

  return (
    <Modal
      modal={modal}
      toggle={toggle}
      modalHeader={
        readOnly
          ? `Ver configuración: ${descripcion || nro || ""}`
          : `Configurar catálogo: ${descripcion || nro || ""}`
      }
      customStyles={dialogStyle}
      handleSubmit={readOnly ? undefined : guardar}
      submitDisabled={guardando}
      edit={!readOnly}
    >
      <div className="d-flex flex-column gap-3 mt-3">
        {/* Card 1 — datos del catálogo */}
        <Card className="shadow-none border">
          <CardBody>
            <h6 className="card-title mb-3">Datos del catálogo</h6>
            <div className="d-flex flex-column gap-2">
              <div style={{ maxWidth: 260 }}>
                <Label className="mb-1">NroTabla</Label>
                <Input
                  type="number"
                  value={nuevoNroTabla ?? ""}
                  onChange={e => {
                    if (readOnly) return
                    const val = e.target.value ? Number(e.target.value) : null
                    setNuevoNroTabla(val)
                  }}
                  disabled={readOnly}
                />
                {nuevoNroTabla != null && nuevoNroTabla !== Number(codNum) && (
                  <small className="text-muted">
                    {tablas.some(
                      t =>
                        t.codNum === nuevoNroTabla &&
                        Number(t.codNum) !== Number(codNum),
                    ) ? (
                      <span className="text-danger">
                        ⚠ Ese número ya está en uso
                      </span>
                    ) : (
                      "Se renombrará el catálogo y todos sus datos"
                    )}
                  </small>
                )}
              </div>
              <div style={{ maxWidth: 260 }}>
                <Label className="mb-1">Orden</Label>
                <Input
                  type="number"
                  value={cfg.orden}
                  onChange={e =>
                    readOnly
                      ? undefined
                      : setCfgField("orden", Number(e.target.value))
                  }
                  disabled={readOnly}
                />
              </div>
              <div>
                <Label className="mb-1">Título</Label>
                <Input
                  value={cfg.titulo}
                  onChange={e =>
                    readOnly ? undefined : setCfgField("titulo", e.target.value)
                  }
                  disabled={readOnly}
                  placeholder="(si se deja vacío usa la descripción del catálogo)"
                />
              </div>
              <div>
                <Label className="mb-1">Descripción</Label>
                <Input
                  value={cfg.descripcion}
                  onChange={e =>
                    readOnly
                      ? undefined
                      : setCfgField("descripcion", e.target.value)
                  }
                  disabled={readOnly}
                />
              </div>
              <div style={{ maxWidth: 260 }}>
                <Label className="mb-1">Modo de borrado</Label>
                <Input
                  type="select"
                  value={cfg.modoBorrado}
                  onChange={e =>
                    readOnly
                      ? undefined
                      : setCfgField("modoBorrado", e.target.value)
                  }
                  disabled={readOnly}
                >
                  <option value="F">Físico</option>
                  <option value="L">Lógico (conserva historia)</option>
                  <option value="N">Protegido (no se puede borrar)</option>
                </Input>
              </div>
              <div className="d-flex gap-4 mt-1 flex-wrap">
                <Label check className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={cfg.permiteAlta}
                    onChange={e =>
                      readOnly
                        ? undefined
                        : setCfgField("permiteAlta", e.target.checked)
                    }
                    disabled={readOnly}
                  />
                  Permite alta
                </Label>
                <Label check className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={cfg.permiteEdicion}
                    onChange={e =>
                      readOnly
                        ? undefined
                        : setCfgField("permiteEdicion", e.target.checked)
                    }
                    disabled={readOnly}
                  />
                  Permite edición
                </Label>
                <Label check className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={cfg.ocultarInactivos}
                    onChange={e =>
                      readOnly
                        ? undefined
                        : setCfgField("ocultarInactivos", e.target.checked)
                    }
                    disabled={readOnly}
                  />
                  Ocultar inactivos
                </Label>
              </div>
              <div style={{ maxWidth: 320 }}>
                <Label className="mb-1">Tipo de catálogo</Label>
                <Input
                  type="select"
                  value={cfg.tipoCatalogo}
                  onChange={e => {
                    if (readOnly) return
                    const tipoAnterior = cfg.tipoCatalogo
                    const nuevoTipo = e.target.value
                    // Los campos son específicos de cada modo (los fijos de
                    // Tabla Interna no tienen sentido en Tabla Externa y
                    // viceversa), pero si ya se habían cargado para este tipo
                    // en esta misma sesión del modal (p.ej. Interna -> Externa
                    // -> Interna) se restauran en vez de perderlos.
                    setCamposPorTipo(prev => ({
                      ...prev,
                      [tipoAnterior]: campos,
                    }))
                    setCfgField("tipoCatalogo", nuevoTipo)
                    if (camposPorTipo[nuevoTipo]) {
                      setCampos(camposPorTipo[nuevoTipo])
                    } else if (nuevoTipo === "NATIVA") {
                      setCampos(CAMPOS_DEFAULT.map(c => ({ ...c })))
                    } else {
                      setCampos([])
                    }
                  }}
                  disabled={readOnly}
                >
                  <option value="NATIVA">Tabla Interna</option>
                  <option value="COMPONENTE_EXTERNO">
                    Basada en Componente
                  </option>
                  <option value="TABLA_EXTERNA">Tabla Externa</option>
                </Input>
              </div>

              {cfg.tipoCatalogo === "COMPONENTE_EXTERNO" && (
                <div style={{ maxWidth: 320 }}>
                  <Label className="mb-1">Componente</Label>
                  <Input
                    type="select"
                    value={cfg.nombreComponente || ""}
                    onChange={e =>
                      readOnly
                        ? undefined
                        : setCfgField(
                            "nombreComponente",
                            e.target.value || null,
                          )
                    }
                    disabled={readOnly}
                  >
                    <option value="">(seleccionar)</option>
                    {COMPONENT_NAMES.map(n => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Input>
                </div>
              )}

              {esTablaExterna && (
                <div className="d-flex gap-3 flex-wrap align-items-end">
                  <div style={{ maxWidth: 220 }}>
                    <Label className="mb-1">Esquema</Label>
                    <Input
                      type="select"
                      value={esquemaSeleccionado}
                      onChange={e =>
                        readOnly
                          ? undefined
                          : handleEsquemaChange(e.target.value)
                      }
                      disabled={readOnly}
                    >
                      <option value="">(seleccionar)</option>
                      {esquemas.map(e => (
                        <option key={e} value={e}>
                          {e}
                        </option>
                      ))}
                    </Input>
                  </div>
                  <div style={{ maxWidth: 280 }}>
                    <Label className="mb-1">Tabla</Label>
                    <Input
                      type="select"
                      value={tablaSeleccionada}
                      onChange={e =>
                        readOnly
                          ? undefined
                          : setTablaSeleccionada(e.target.value)
                      }
                      disabled={readOnly || !esquemaSeleccionado}
                    >
                      <option value="">(seleccionar)</option>
                      {tablasDelEsquema.map(t => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </Input>
                  </div>
                  {!readOnly && (
                    <Button
                      color="light"
                      className="shadow-none"
                      disabled={!tablaSeleccionada || importando}
                      onClick={importarColumnas}
                    >
                      {importando ? "Importando..." : "Importar columnas"}
                    </Button>
                  )}
                </div>
              )}

              {pendienteActivo && (
                <Alert color="warning" className="mb-0 mt-2" fade={false}>
                  Esta tabla no tiene columna <code>activo</code>. ¿El borrado
                  de filas será lógico o físico?
                  <div className="d-flex gap-2 mt-2">
                    <Button
                      size="sm"
                      color="secondary"
                      disabled={importando}
                      onClick={() => confirmarActivo("L")}
                    >
                      Lógico (agrega columna &quot;activo&quot;)
                    </Button>
                    <Button
                      size="sm"
                      color="danger"
                      outline
                      disabled={importando}
                      onClick={() => confirmarActivo("F")}
                    >
                      Físico
                    </Button>
                    <Button
                      size="sm"
                      color="light"
                      className="shadow-none"
                      disabled={importando}
                      onClick={() => setPendienteActivo(null)}
                    >
                      Cancelar
                    </Button>
                  </div>
                </Alert>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Card 2 — campos (catálogos nativos y de tabla externa; con componente los define el componente) */}
        {cfg.tipoCatalogo !== "COMPONENTE_EXTERNO" && (
          <Card className="shadow-none border">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title mb-0">Campos</h6>
                {!readOnly && !esTablaExterna && (
                  <Button
                    color="light"
                    className="shadow-none"
                    onClick={agregarCampo}
                  >
                    Agregar campo
                  </Button>
                )}
              </div>
              <Table responsive size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Campo</th>
                    <th>Etiqueta</th>
                    <th className="text-center">Grilla</th>
                    <th className="text-center">Form</th>
                    <th className="text-center">Editable</th>
                    <th className="text-center">Autoincr.</th>
                    <th className="text-center">Requerido</th>
                    <th className="text-center">Filtro</th>
                    <th>Formato</th>
                    <th style={{ width: 90 }}>Orden</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {campos.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-muted text-center py-3">
                        {esTablaExterna
                          ? 'Sin campos importados. Seleccioná esquema y tabla, y usá "Importar columnas".'
                          : "Sin campos configurados (se mostrarán los 6 genéricos por defecto)."}
                      </td>
                    </tr>
                  )}
                  {campos.map((c, i) => (
                    <React.Fragment key={i}>
                      <tr>
                        <td>
                          {esTablaExterna ? (
                            <Input bsSize="sm" value={c.campo} disabled />
                          ) : (
                            <Input
                              type="select"
                              bsSize="sm"
                              value={c.campo}
                              onChange={e =>
                                readOnly
                                  ? undefined
                                  : setCampoField(i, "campo", e.target.value)
                              }
                              disabled={readOnly}
                            >
                              {CAMPOS.map(opt => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </Input>
                          )}
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            value={c.etiqueta}
                            onChange={e =>
                              readOnly
                                ? undefined
                                : setCampoField(i, "etiqueta", e.target.value)
                            }
                            disabled={readOnly}
                          />
                        </td>
                        <td className="text-center">
                          <Input
                            type="checkbox"
                            checked={c.visibleGrilla}
                            onChange={e =>
                              readOnly
                                ? undefined
                                : setCampoField(
                                    i,
                                    "visibleGrilla",
                                    e.target.checked,
                                  )
                            }
                            disabled={readOnly}
                          />
                        </td>
                        <td className="text-center">
                          <Input
                            type="checkbox"
                            checked={c.visibleForm}
                            onChange={e =>
                              readOnly
                                ? undefined
                                : setCampoField(
                                    i,
                                    "visibleForm",
                                    e.target.checked,
                                  )
                            }
                            disabled={readOnly}
                          />
                        </td>
                        <td className="text-center">
                          <Input
                            type="checkbox"
                            checked={c.editable}
                            onChange={e =>
                              readOnly
                                ? undefined
                                : setCampoField(i, "editable", e.target.checked)
                            }
                            disabled={
                              readOnly ||
                              c.autoincremento ||
                              (!c.visibleGrilla && !c.visibleForm)
                            }
                          />
                        </td>
                        <td className="text-center">
                          {(c.campo === "codNum" || esTablaExterna) && (
                            <Input
                              type="checkbox"
                              checked={c.autoincremento}
                              title="Al agregar una fila, el sistema asigna el mayor Id + 1"
                              onChange={e =>
                                readOnly
                                  ? undefined
                                  : setCampoField(
                                      i,
                                      "autoincremento",
                                      e.target.checked,
                                    )
                              }
                              disabled={readOnly}
                            />
                          )}
                        </td>
                        <td className="text-center">
                          <Input
                            type="checkbox"
                            checked={c.requerido}
                            onChange={e =>
                              readOnly
                                ? undefined
                                : setCampoField(
                                    i,
                                    "requerido",
                                    e.target.checked,
                                  )
                            }
                            disabled={readOnly}
                          />
                        </td>
                        <td className="text-center">
                          <Input
                            type="checkbox"
                            checked={c.filtro || false}
                            title="Muestra un filtro por este campo arriba de la grilla del catálogo"
                            onChange={e =>
                              readOnly
                                ? undefined
                                : setCampoField(i, "filtro", e.target.checked)
                            }
                            disabled={readOnly}
                          />
                        </td>
                        <td>
                          <Input
                            type="select"
                            bsSize="sm"
                            value={c.formato}
                            onChange={e =>
                              readOnly
                                ? undefined
                                : setCampoField(i, "formato", e.target.value)
                            }
                            disabled={readOnly || c.autoincremento}
                          >
                            {(c.formato && !FORMATOS.includes(c.formato)
                              ? [...FORMATOS, c.formato]
                              : FORMATOS
                            ).map(opt => (
                              <option key={opt} value={opt}>
                                {opt === "" ? "(ninguno)" : opt}
                              </option>
                            ))}
                          </Input>
                        </td>
                        <td>
                          <Input
                            type="number"
                            bsSize="sm"
                            value={c.orden}
                            onChange={e =>
                              readOnly
                                ? undefined
                                : setCampoField(i, "orden", e.target.value)
                            }
                            disabled={readOnly}
                          />
                        </td>
                        <td>
                          <button
                            className="mdi mdi-delete-outline font-size-18 text-danger border-0 bg-transparent p-0"
                            title="Quitar campo"
                            onClick={() =>
                              readOnly ? undefined : quitarCampo(i)
                            }
                            disabled={readOnly}
                            style={{ display: readOnly ? "none" : "inline" }}
                          />
                        </td>
                      </tr>
                      {c.formato === "combo" && (
                        <tr>
                          <td colSpan={11} className="bg-light">
                            <div className="d-flex gap-3 flex-wrap py-1">
                              <div style={{ maxWidth: 260 }}>
                                <Label className="mb-1">Catálogo origen</Label>
                                <Input
                                  type="select"
                                  bsSize="sm"
                                  value={c.comboNroTabla ?? ""}
                                  onChange={e => {
                                    if (readOnly) return
                                    const valor = e.target.value
                                      ? Number(e.target.value)
                                      : null
                                    // Las 3 actualizaciones deben aplicarse de forma
                                    // atómica: encadenar 3 llamadas a setCampoField
                                    // acá pisaría el cambio de comboNroTabla, porque
                                    // cada una parte del mismo `campos` (closure
                                    // desactualizado) del render actual.
                                    setCampos(prevCampos =>
                                      prevCampos.map((c, idx) =>
                                        idx === i
                                          ? {
                                              ...c,
                                              comboNroTabla: valor,
                                              comboCampoEtiqueta: "",
                                              comboCampoValor: "",
                                            }
                                          : c,
                                      ),
                                    )
                                    cargarCamposDeCatalogo(valor)
                                  }}
                                  disabled={readOnly}
                                >
                                  <option value="">(seleccionar)</option>
                                  {catalogosDisponibles
                                    .filter(
                                      cat =>
                                        Number(cat.codNum) !== Number(codNum),
                                    )
                                    .map(cat => (
                                      <option
                                        key={cat.codNum}
                                        value={cat.codNum}
                                      >
                                        {cat.titulo || cat.descripcion}
                                      </option>
                                    ))}
                                </Input>
                              </div>
                              <div style={{ maxWidth: 220 }}>
                                <Label className="mb-1">Campo a mostrar</Label>
                                <Input
                                  type="select"
                                  bsSize="sm"
                                  value={c.comboCampoEtiqueta || ""}
                                  onChange={e =>
                                    readOnly
                                      ? undefined
                                      : setCampoField(
                                          i,
                                          "comboCampoEtiqueta",
                                          e.target.value,
                                        )
                                  }
                                  disabled={readOnly || !c.comboNroTabla}
                                >
                                  <option value="">(seleccionar)</option>
                                  {(
                                    camposPorCatalogo[c.comboNroTabla] || []
                                  ).map(cc => (
                                    <option key={cc.campo} value={cc.campo}>
                                      {cc.etiqueta || cc.campo}
                                    </option>
                                  ))}
                                </Input>
                              </div>
                              <div style={{ maxWidth: 220 }}>
                                <Label className="mb-1">Campo a guardar</Label>
                                <Input
                                  type="select"
                                  bsSize="sm"
                                  value={c.comboCampoValor || ""}
                                  onChange={e =>
                                    readOnly
                                      ? undefined
                                      : setCampoField(
                                          i,
                                          "comboCampoValor",
                                          e.target.value,
                                        )
                                  }
                                  disabled={readOnly || !c.comboNroTabla}
                                >
                                  <option value="">(seleccionar)</option>
                                  {(
                                    camposPorCatalogo[c.comboNroTabla] || []
                                  ).map(cc => (
                                    <option key={cc.campo} value={cc.campo}>
                                      {cc.etiqueta || cc.campo}
                                    </option>
                                  ))}
                                </Input>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        )}
      </div>
    </Modal>
  )
}

ConfigCatalogoModal.propTypes = {
  nro: PropTypes.any,
  codNum: PropTypes.any,
  descripcion: PropTypes.string,
  modal: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  readOnly: PropTypes.bool,
  tablas: PropTypes.array,
}

export default ConfigCatalogoModal
